import * as React from 'react';
import './schemaFieldTypeahead.scss';
import { Schema, SchemaField } from '../../types/Metadata';
import { Typeahead } from '../Typeahead/typeahead';
import { SROpts } from '../../types/SROpts';
import { cn, SchemalessFilterSchema } from '../../utils';
import { useSelector } from '../../utils/useSelector';
import { Add } from '../Icon/icon';

interface Props {
  selected?: SchemaField | string; // ex: Item: Title
  isChecked?: (f: SchemaField) => boolean;
  canAddAnother?: (f: SchemaField) => boolean;
  excludeFields?:
    | SchemaField[]
    | string[]
    | ((f: SchemaField, s?: Schema) => boolean); // ex: Item.title
  onSelectField?: (f: SchemaField, opts?: SROpts) => boolean | any;
  onSelectAddAgain?: (f: SchemaField, opts?: SROpts) => boolean | any;
  onSelectSchema?: (s: Schema, opts?: SROpts) => boolean | any;
  onTextChange?: (t: string) => void;
  inline?: boolean;
  flat?: boolean;
  disabled?: boolean;
  removable?: boolean;
  includeSchemalessFilters?: boolean;
  children?: any;
}

export const SchemaFieldTypeahead = ({
  selected,
  excludeFields,
  includeSchemalessFilters,
  children,
  onTextChange,
  removable,
  disabled,
  inline,
  flat,
  ...props
}: Props) => {
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const onRawTextChange = React.useCallback(
    t => {
      if (!t && removable) {
        if (props.onSelectField) props.onSelectField(null);

        if (props.onSelectAddAgain) props.onSelectAddAgain(null);
        if (props.onSelectSchema) props.onSelectSchema(null);
      }
    },
    [
      removable,
      props.onSelectField,
      props.onSelectSchema,
      props.onSelectAddAgain
    ]
  );

  const options = React.useMemo(() => {
    const excludes = [];
    if (Array.isArray(excludeFields)) {
      excludeFields?.forEach(f => {
        if (typeof f == 'string') excludes.push(f);
        if (f.key) excludes.push(f.key);
      });
    } else if (typeof excludeFields == 'function') {
      schemas.forEach(s => {
        s?.props?.fields?.forEach(f => {
          if (excludeFields(f, s)) {
            if (f.key) excludes.push(f.key);
          }
        });
      });
    }

    const options = schemas.reduce((p, c) => {
      c.props.fields.forEach(f => {
        if (!excludes.includes(f.key)) p.push({ schema: c, field: f });
      });
      return p;
    }, [] as { schema: Schema; field: SchemaField }[]);

    if (includeSchemalessFilters) {
      SchemalessFilterSchema.props.fields.forEach(f => {
        if (!excludes.includes(f.key)) {
          options.push({ schema: SchemalessFilterSchema, field: f });
        }
      });
    }
    return options;
  }, [schemas, excludeFields, includeSchemalessFilters]);

  const value = typeof selected == 'string' ? selected : selected?.displayName;
  return (
    <Typeahead
      className={cn('typeahead-schema-field', {
        'typeahead-check': !!props.isChecked,
        'typeahead-tree': !flat
      })}
      inline={inline}
      options={options}
      value={value}
      {...{ children, onTextChange, onRawTextChange }}
      disabled={disabled}
      renderSearchResult={(matched, opts) => {
        const { noMatchMessage, text, children } = opts;
        if (!!text && matched?.length === 0) {
          return (
            <>
              {!!children && <div>{children}</div>}
              {noMatchMessage}
            </>
          );
        }
        const _schemas = (matched?.length ? matched : options).reduce<Schemas>(
          (p, { schema, field }, idx) => {
            const s = p.filter(_p => _p.s.id == schema.id)[0];
            if (!s) {
              p.push({ s: schema, fields: [field] });
            } else {
              s.fields.push(field);
            }
            if (idx == matched.length - 1) {
              p.sort((a, b) => a.s.props.name?.localeCompare(b.s.props.name));
              p.forEach(({ fields }) =>
                fields.sort((a, b) => a.name?.localeCompare(b.name))
              );
            }
            return p;
          },
          []
        );
        const List = flat ? FlatList : Tree;
        return (
          <>
            {!!children && <div>{children}</div>}
            <div className="scrollable">
              <List schemas={_schemas} {...props} opts={opts} />
            </div>
          </>
        );
      }}
      includes={(t, { field }) =>
        field.displayName
          ?.toLowerCase()
          .replaceAll(':', '')
          .includes(t?.trim().toLowerCase().replaceAll(':', ''))
      }
    />
  );
};

type Schemas = { s: Schema; fields: SchemaField[] }[];
type FieldProps = Pick<
  Props,
  | 'onSelectSchema'
  | 'onSelectField'
  | 'isChecked'
  | 'onSelectAddAgain'
  | 'canAddAnother'
> & {
  opts: SROpts;
  schemas: Schemas;
};

const FlatList = ({
  onSelectField,
  opts,
  schemas,
  isChecked,
  canAddAnother,
  onSelectAddAgain
}: FieldProps) => {
  return (
    <ul className="dd-list">
      {schemas.reduce((li, { s, fields }) => {
        fields.forEach(f => {
          const canAddAnotherPill = canAddAnother && canAddAnother(f);
          li.push(
            <li
              className={cn({ 'dd-checked': isChecked && isChecked(f) })}
              key={f.key}
              onClick={
                !onSelectField
                  ? null
                  : () => {
                      if (!onSelectField(f, opts)) opts.hideDropdown();
                    }
              }
            >
              <span>{f.displayName}</span>
              {canAddAnotherPill && (
                <span
                  onClick={e => {
                    e.stopPropagation();
                    if (onSelectAddAgain) {
                      if (!onSelectAddAgain(f, opts)) opts.hideDropdown();
                    }
                  }}
                  title={'Add Another'}
                >
                  <Add />
                </span>
              )}
            </li>
          );
        });
        return li;
      }, [])}
    </ul>
  );
};

const Tree = ({
  onSelectSchema,
  onSelectField,
  opts,
  schemas,
  isChecked
}: FieldProps) => {
  return (
    <ul className="dd-list">
      {schemas.map(({ s, fields }) => {
        return (
          <li
            key={s.id}
            className={cn({ 'dd-inactive': !onSelectSchema })}
            onClick={
              !onSelectSchema
                ? null
                : () => {
                    if (!onSelectSchema(s, opts)) opts.hideDropdown();
                  }
            }
          >
            <span>{s.props.name}</span>
            <ul className="dd-list">
              {fields.map(f => (
                <li
                  key={f.key}
                  className={cn({
                    'dd-inactive': !onSelectField,
                    'dd-checked': isChecked && isChecked(f)
                  })}
                  onClick={
                    !onSelectField
                      ? null
                      : () => {
                          if (!onSelectField(f, opts)) opts.hideDropdown();
                        }
                  }
                >
                  {f.name}
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );
};
